import React from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import DeltaPill from './DeltaPill'
import Icon from '../icon/Icon'
import Text from '../Text'
import ValueBar from '../ValueBar'
import { ProgressBar } from '../ProgressBar'

// Utils
import { useAppState } from '../../context/AppContext'

// Styles
import { mqFrom, mqTo } from '../../styles/mediaQueries'
import {
  gradientBackground,
  shadows,
  fastTransition,
} from '../../styles/effects'

// Types
import { AnyColor } from '../../common/models/colors'
import { StatCardRevealState } from '../evaluations/MemberStatBlocks'
import { IconType } from '../icon/IconType'

export interface IStatCardProps {
  icon: IconType
  titleKey: string
  value?: number
  // Goals append a "%", skills & culture a "/ 100" scale (showScale).
  suffix?: string
  showScale?: boolean
  // Qualitative line under the score, e.g. "Meets expectations" or
  // "3 out of 4 goals achieved".
  subtitle?: string
  // Delta pill (vs the previous evaluation period).
  current?: number
  previous?: number
  compareActive?: boolean
  // Segmented bar (skills/culture). When omitted the bar falls back to the
  // continuous score/100 fill used by the cohort KPI strip.
  segments?: { total: number; complete: number; color?: AnyColor }
  // Submitted-count row, e.g. "3/5 submitted". Goals pass a ready countText
  // ("3 out of 4 goals achieved") instead.
  measured?: number
  total?: number
  countLabelKey?: string
  countText?: string
  // Slot below the bar, used for the row of assessor avatars.
  children?: React.ReactNode
  // Framed only: replaces the result/bar region with custom content while
  // keeping the header, chevron and avatar strip (e.g. the feedback tips/tops
  // tallies). Ignored in the pending/empty muted states.
  body?: React.ReactNode
  // Framed layout (collection page + evaluation overview modal): a white inner
  // container holding the stat, sitting on a light frame with the avatar strip
  // on the frame. It reads by its subject: a quiet small-caps header, the
  // qualitative result leading and the score as a side note.
  // Default (KPI strip) keeps the flat single-surface card.
  framed?: boolean
  // When set the card becomes clickable: job-card hover state + a chevron in the
  // header that drills into the matching detail view.
  onClick?: () => void
  // Visual reveal mode for gated results. Defaults to 'revealed'. The gated
  // states (pending/empty/noScore) always render in the framed style.
  revealState?: StatCardRevealState
  // Quiet placeholder copy shown in the 'pending' state (caller derives it from
  // the reveal reason).
  pendingText?: string
  // Quiet placeholder copy shown in the 'empty' state ("no results this round").
  emptyText?: string
  // Label + hint shown in the 'noScore' state, in place of the value/bar.
  noScoreText?: string
  noScoreHint?: string
  className?: string
}

const clampPercentage = (value: number) => Math.max(0, Math.min(100, value))

const StatCard: React.FC<IStatCardProps> = ({
  icon,
  titleKey,
  value,
  suffix,
  showScale,
  subtitle,
  current,
  previous,
  compareActive,
  segments,
  measured,
  total,
  countLabelKey = 'termEvaluationsProgress.evalsCompleted',
  countText,
  children,
  body,
  framed,
  onClick,
  revealState = 'revealed',
  pendingText,
  emptyText,
  noScoreText,
  noScoreHint,
  className,
}) => {
  const { t } = useTranslation()
  const { theme } = useAppState()

  const isPending = revealState === 'pending'
  const isEmpty = revealState === 'empty'
  const isNoScore = revealState === 'noScore'
  const hideNumbers = isPending || isEmpty || isNoScore

  const hasValue = typeof value === 'number'
  const scorePercentage = hasValue ? clampPercentage(value) : 0
  // A previous-term marker/delta only makes sense against a current value —
  // without one they read as misleading (a stray marker, a "-" pill).
  const showPrevMarker =
    compareActive && hasValue && typeof previous === 'number'
  const prevPercentage = showPrevMarker ? clampPercentage(previous) : 0

  // Delta shows whenever a current and previous value exist; gated by
  // compareActive only in the flat (KPI) layout, always-on in the framed
  // overview modal. Suppressed whenever numbers are hidden (pending/empty/
  // noScore).
  const showDelta =
    !hideNumbers &&
    hasValue &&
    typeof previous === 'number' &&
    (framed || compareActive)

  const valueText = hasValue ? `${Math.round(value)}${suffix ?? ''}` : '-'
  const scoreColor = hasValue ? theme.colors.primary : theme.colors.textLighter

  const chevron = onClick && (
    <Chevron>
      <Icon type="ChevronRight" size={10} color={theme.colors.primary} />
    </Chevron>
  )

  // Framed header: a quiet small-caps label that stays identical in every
  // reveal state, so the card always reads by its subject.
  const framedHeader = (
    <FramedHeader>
      <Icon type={icon} size={12} color={theme.colors.textLight} />
      <Text textStyle="labelSmallCaps" color="textLight" tag="span">
        {t(titleKey)}
      </Text>
      {chevron}
    </FramedHeader>
  )

  // Framed body: the qualitative result leads ("Meets expectations", "3 out of
  // 4 goals achieved"), the score and delta sit beside it as a side note.
  const framedBody = isNoScore ? (
    // Result-card shell without an aggregate score: a short label + hint sit
    // where the result/bar would be; the chevron and avatar strip keep it
    // reading as an openable result card.
    <FramedBottom>
      <Text textStyle="title" color="textDark" tag="span">
        {noScoreText}
      </Text>
      {noScoreHint && (
        <Text textStyle="caption" color="textLight" tag="span">
          {noScoreHint}
        </Text>
      )}
    </FramedBottom>
  ) : (
    <FramedBottom $gap={8}>
      <ResultRow>
        <Text textStyle="title" color="textDark" tag="span">
          {subtitle}
        </Text>
        <Value>
          <Text textStyle="labelHeavy" color="text" tag="span">
            {valueText}
          </Text>
          {hasValue && showScale && (
            <Text textStyle="caption" color="textLight" tag="span">
              / 100
            </Text>
          )}
          {showDelta && (
            <DeltaPill
              current={current ?? value}
              previous={previous}
              size="small"
            />
          )}
        </Value>
      </ResultRow>
      {segments ? (
        <ValueBar
          total={segments.total}
          complete={segments.complete}
          color={segments.color ?? 'primary'}
          gap={4}
          trackColor={theme.colors.textLightest}
        />
      ) : (
        <BarTrack>
          <ScoreBar
            color={scoreColor}
            backgroundColor={theme.colors.textLightest}
            percentage={scorePercentage}
            height={8}
          />
          {showPrevMarker && (
            <PrevMarker $thin style={{ left: `${prevPercentage}%` }} />
          )}
        </BarTrack>
      )}
    </FramedBottom>
  )

  // Framed cards and every gated state share the framed header. Pending and
  // empty center a muted state icon above the quiet message: Hide ("results
  // stay hidden until you assess") for pending, the section's own icon for an
  // empty round.
  const content =
    framed || hideNumbers ? (
      <>
        {framedHeader}
        {isPending || isEmpty ? (
          <MutedBody>
            <Icon
              type={isPending ? 'Hide' : icon}
              size={24}
              color={theme.colors.text72}
            />
            <MutedMessage textStyle="bodySmall" color="textLight" tag="div">
              {isEmpty ? emptyText : pendingText}
            </MutedMessage>
          </MutedBody>
        ) : (
          body ?? framedBody
        )}
      </>
    ) : (
      <>
        <Header>
          <IconBox>
            <Icon type={icon} size={24} color={theme.colors.primary} />
          </IconBox>
          <TitleGroup>
            <Text textStyle="labelSmallCaps" color="textLight" tag="span">
              {t('general.averages')}
            </Text>
            <Title textStyle="title" tag="span">
              {t(titleKey)}
            </Title>
          </TitleGroup>
          {chevron}
        </Header>

        <ValueRow>
          <Value>
            <Text textStyle="displayM" color="primary" tag="span">
              {valueText}
            </Text>
            {hasValue && showScale && (
              <Text textStyle="caption" color="textLighter" tag="span">
                / 100
              </Text>
            )}
          </Value>
          {showDelta && (
            <DeltaPill
              current={current ?? value}
              previous={previous}
              size="regular"
            />
          )}
        </ValueRow>

        {/* Qualitative label under the score. */}
        {subtitle && (
          <Text textStyle="title" color="textLight" tag="span">
            {subtitle}
          </Text>
        )}

        <BarBlock>
          {segments ? (
            <ValueBar
              total={segments.total}
              complete={segments.complete}
              color={segments.color ?? 'primary'}
            />
          ) : (
            <BarTrack>
              <ScoreBar
                color={scoreColor}
                backgroundColor={theme.colors.lightest}
                percentage={scorePercentage}
              />
              {showPrevMarker && (
                <PrevMarker style={{ left: `${prevPercentage}%` }} />
              )}
            </BarTrack>
          )}
          {countText ? (
            <CountRow>
              <Text textStyle="caption" color="textLight" tag="span">
                {countText}
              </Text>
            </CountRow>
          ) : (
            typeof measured === 'number' &&
            typeof total === 'number' && (
              <CountRow>
                <Icon type="People" size={14} color={theme.colors.textLight} />
                <Text textStyle="caption" color="textLight" tag="span">
                  {`${measured}/${total} ${t(countLabelKey)}`}
                </Text>
              </CountRow>
            )
          )}
        </BarBlock>
      </>
    )

  if (framed) {
    return (
      <Card
        className={className}
        $framed
        $clickable={!!onClick}
        onClick={onClick}
      >
        <Content $clickable={!!onClick}>{content}</Content>
        {children && <AvatarStrip>{children}</AvatarStrip>}
      </Card>
    )
  }

  return (
    <Card className={className} $clickable={!!onClick} onClick={onClick}>
      {content}
      {children}
    </Card>
  )
}

export default StatCard

const clickableStyles = css`
  border: 1px solid ${({ theme }) => theme.colors.textLightest};
  transition: all ${fastTransition};

  &:hover {
    background: ${({ theme }) => gradientBackground(theme)};
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${shadows.depthThree};
  }
`

const Card = styled.div<{ $framed?: boolean; $clickable?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.textLightest};
  ${({ $clickable }) => $clickable && 'cursor: pointer;'}

  ${({ $framed }) =>
    $framed
      ? css`
          border-radius: 12px;
        `
      : css`
          gap: 12px;
          padding: 16px;
          border-radius: 8px;

          ${mqFrom.L`
            padding: 24px;
          `}
        `}

  // Framed cards put the hover treatment on the inner Content; flat cards here.
  ${({ $clickable, $framed }) => $clickable && !$framed && clickableStyles}
`

// Framed-only: the white inner surface holding the stat content.
const Content = styled.div<{ $clickable?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 16px;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.textLightest};
  background-color: ${({ theme }) => theme.colors.lightest};

  ${({ $clickable }) => $clickable && clickableStyles}

  /* Tighter padding + a slightly darker, more defined border on mobile to match
     the overview cards. */
  ${mqTo.M`
    padding: 16px;
    ${({ theme }) => `border-color: ${theme.colors.textLighter};`}
  `}
`

// Framed-only: assessor avatars sit on the frame, below the white content,
// left-aligned and vertically centered within the grey strip. A second child
// (the assessor count) is pushed to the right edge by space-between; a lone
// avatar group still sits left.
const AvatarStrip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 16px 12px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const FramedHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > span:first-child {
    margin-right: 0;
  }
`

// Drill-in affordance: primary at 50%, full on card hover. Aligned to the right
// of the header row.
const Chevron = styled.div`
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  opacity: 0.5;
  transition: opacity ${fastTransition};

  > span {
    margin-right: 0;
  }

  ${Card}:hover &,
  ${Content}:hover & {
    opacity: 1;
  }
`

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  // Allow the flex item to shrink below its content size so longer
  // translations wrap inside the card instead of overflowing it.
  min-width: 0;
`

const Title = styled(Text)`
  overflow-wrap: anywhere;
`

const IconBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.colors.primaryLight};

  > span {
    margin-right: 0;
  }
`

const ValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
`

const Value = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`

// Framed result line: the qualitative result left, the score + delta right.
const ResultRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px 12px;
`

// Pending/empty: the muted icon + message centered below the header.
const MutedBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 80px;
  text-align: center;

  // Drop the Icon's default right margin so it sits dead-centre.
  > span:first-child {
    margin-right: 0;
  }
`

const MutedMessage = styled(Text)`
  max-width: 26ch;
  text-align: center;
`

const BarBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

// Framed result region, pinned to the bottom of the white card so paired
// cards line up their bars (or the 'noScore' label + hint) whatever the height.
const FramedBottom = styled.div<{ $gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap = 4 }) => $gap}px;
  margin-top: auto;
`

const BarTrack = styled.div`
  position: relative;
`

// Square off the fill's right edge so the exact score position reads as a
// crisp vertical line; the left edge stays rounded to match the track.
const ScoreBar = styled(ProgressBar)`
  > div {
    border-radius: 50px 0 0 50px;
  }
`

// Vertical tick marking where the previous evaluation period averaged,
// centered on the 12px bar (or the framed 8px one).
const PrevMarker = styled.div<{ $thin?: boolean }>`
  position: absolute;
  top: ${({ $thin }) => ($thin ? -4 : -2)}px;
  width: 2px;
  height: 16px;
  border-radius: 1px;
  transform: translateX(-1px);
  z-index: 1;
  background-color: ${({ theme }) => theme.colors.text};
`

const CountRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  > span:first-child {
    margin-right: 0;
  }
`
